/**
 * Search Params Encoding Utilities
 *
 * Encodes/decodes filter state to/from URL search parameters
 * for filter persistence across navigation and page refresh.
 *
 * URL Parameter Scheme:
 * - q     : Search query
 * - r     : Regions (comma-separated)
 * - s     : Sectors (comma-separated)
 * - ft    : Funding types/sources (comma-separated)
 * - fi    : Funding instruments (comma-separated)
 * - ar    : Amount ranges (comma-separated)
 * - ds    : Deadline statuses (comma-separated)
 * - o     : Organizations (comma-separated)
 * - sg    : Sustainability goals (comma-separated)
 * - cf    : Confidence filter (comma-separated numbers)
 * - sort  : Sort option
 */

export interface FilterState {
  regions: string[];
  eligibleCountry: string;
  sectors: string[];
  fundingSources: string[];
  fundingInstruments: string[];
  organizations: string[];
  amountRanges: string[];
  documentsRequired: string[];
  applicationFormats: string[];
  sustainabilityGoals: string[];
  deadlineStatuses: string[];
  confidenceFilter: number[];
}

export type SortOption = 'default' | 'relevance' | 'amount-high' | 'amount-low' | 'deadline-least' | 'deadline-most' | 'newest' | 'oldest';

const PARAM_KEYS = {
  query: 'q',
  regions: 'r',
  sectors: 's',
  fundingSources: 'ft',
  fundingInstruments: 'fi',
  amountRanges: 'ar',
  deadlineStatuses: 'ds',
  organizations: 'o',
  sustainabilityGoals: 'sg',
  confidenceFilter: 'cf',
  sort: 'sort',
} as const;

/**
 * Encodes filter state and search params to URL search parameters
 */
export function encodeFiltersToParams(
  filters: FilterState,
  searchQuery: string,
  sortBy: SortOption
): URLSearchParams {
  const params = new URLSearchParams();

  // Search query
  if (searchQuery.trim()) {
    params.set(PARAM_KEYS.query, searchQuery.trim());
  }

  // Array filters - only add if non-empty
  if (filters.regions.length > 0) {
    params.set(PARAM_KEYS.regions, filters.regions.join(','));
  }
  if (filters.sectors.length > 0) {
    params.set(PARAM_KEYS.sectors, filters.sectors.join(','));
  }
  if (filters.fundingSources.length > 0) {
    params.set(PARAM_KEYS.fundingSources, filters.fundingSources.join(','));
  }
  if (filters.fundingInstruments.length > 0) {
    params.set(PARAM_KEYS.fundingInstruments, filters.fundingInstruments.join(','));
  }
  if (filters.amountRanges.length > 0) {
    params.set(PARAM_KEYS.amountRanges, filters.amountRanges.join(','));
  }
  if (filters.deadlineStatuses.length > 0) {
    params.set(PARAM_KEYS.deadlineStatuses, filters.deadlineStatuses.join(','));
  }
  if (filters.organizations.length > 0) {
    params.set(PARAM_KEYS.organizations, filters.organizations.join(','));
  }
  if (filters.sustainabilityGoals.length > 0) {
    params.set(PARAM_KEYS.sustainabilityGoals, filters.sustainabilityGoals.join(','));
  }
  if (filters.confidenceFilter.length > 0) {
    params.set(PARAM_KEYS.confidenceFilter, filters.confidenceFilter.join(','));
  }

  // Sort - only add if not default
  if (sortBy !== 'default') {
    params.set(PARAM_KEYS.sort, sortBy);
  }

  return params;
}

/**
 * Creates the default empty filter state
 */
export function getDefaultFilters(): FilterState {
  return {
    regions: [],
    eligibleCountry: '',
    sectors: [],
    fundingSources: [],
    fundingInstruments: [],
    organizations: [],
    amountRanges: [],
    documentsRequired: [],
    applicationFormats: [],
    sustainabilityGoals: [],
    deadlineStatuses: [],
    confidenceFilter: [],
  };
}

/**
 * Decodes URL search parameters to filter state
 */
export function decodeParamsToFilters(params: URLSearchParams): {
  filters: FilterState;
  searchQuery: string;
  sortBy: SortOption;
} {
  const filters = getDefaultFilters();

  // Parse array params (comma-separated)
  const parseArray = (key: string): string[] => {
    const value = params.get(key);
    if (!value) return [];
    return value.split(',').map(s => s.trim()).filter(Boolean);
  };

  // Parse number array
  const parseNumberArray = (key: string): number[] => {
    const value = params.get(key);
    if (!value) return [];
    return value
      .split(',')
      .map(s => parseInt(s.trim(), 10))
      .filter(n => !isNaN(n));
  };

  // Decode filters
  filters.regions = parseArray(PARAM_KEYS.regions);
  filters.sectors = parseArray(PARAM_KEYS.sectors);
  filters.fundingSources = parseArray(PARAM_KEYS.fundingSources);
  filters.fundingInstruments = parseArray(PARAM_KEYS.fundingInstruments);
  filters.amountRanges = parseArray(PARAM_KEYS.amountRanges);
  filters.deadlineStatuses = parseArray(PARAM_KEYS.deadlineStatuses);
  filters.organizations = parseArray(PARAM_KEYS.organizations);
  filters.sustainabilityGoals = parseArray(PARAM_KEYS.sustainabilityGoals);
  filters.confidenceFilter = parseNumberArray(PARAM_KEYS.confidenceFilter);

  // Decode search query
  const searchQuery = params.get(PARAM_KEYS.query) || '';

  // Decode sort
  const sortValue = params.get(PARAM_KEYS.sort);
  const validSorts: SortOption[] = ['default', 'relevance', 'amount-high', 'amount-low', 'deadline-least', 'deadline-most', 'newest', 'oldest'];
  const sortBy: SortOption = validSorts.includes(sortValue as SortOption)
    ? (sortValue as SortOption)
    : 'default';

  return { filters, searchQuery, sortBy };
}

/**
 * Checks if filters have any active values
 */
export function hasActiveFilters(filters: FilterState): boolean {
  return (
    filters.regions.length > 0 ||
    filters.sectors.length > 0 ||
    filters.fundingSources.length > 0 ||
    filters.fundingInstruments.length > 0 ||
    filters.organizations.length > 0 ||
    filters.amountRanges.length > 0 ||
    filters.deadlineStatuses.length > 0 ||
    filters.sustainabilityGoals.length > 0 ||
    filters.confidenceFilter.length > 0 ||
    filters.eligibleCountry !== ''
  );
}

/**
 * Counts the number of active filter values
 */
export function countActiveFilters(filters: FilterState): number {
  return (
    filters.regions.length +
    filters.sectors.length +
    filters.fundingSources.length +
    filters.fundingInstruments.length +
    filters.organizations.length +
    filters.amountRanges.length +
    filters.deadlineStatuses.length +
    filters.sustainabilityGoals.length +
    filters.confidenceFilter.length +
    (filters.eligibleCountry ? 1 : 0)
  );
}
